import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import '../styles/Premium.css';

const formatNaira = (amount) => `₦${amount.toLocaleString('en-NG')}`;

const PLAN_LABELS = {
  monthly: { name: 'Monthly', price: 1300, period: 'per month' },
  annual: { name: 'Annual', price: 10000, period: 'per year' },
};

export default function ManageSubscriptionPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    API.get('/api/billing/status')
      .then((res) => {
        if (!cancelled) setStatus(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await API.post('/api/billing/cancel');
      // Refresh status
      const res = await API.get('/api/billing/status');
      setStatus(res.data);
      setCancelConfirm(false);
      alert('Your subscription has been cancelled. You\'ll keep access until the end of the current billing period.');
    } catch (err) {
      if (err.response?.status === 501) {
        alert('Cancellation is not yet configured. This will be enabled soon.');
      } else {
        alert(err.response?.data?.message || 'Failed to cancel subscription');
      }
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="premium-page">
        <header className="premium-header">
          <button className="premium-back" onClick={() => navigate(-1)} aria-label="Back">←</button>
          <h1>Subscription</h1>
          <div className="premium-spacer" />
        </header>
        <div className="manage-loading">Loading...</div>
      </div>
    );
  }

  if (!status?.is_premium) {
    return (
      <div className="premium-page">
        <header className="premium-header">
          <button className="premium-back" onClick={() => navigate(-1)} aria-label="Back">←</button>
          <h1>Subscription</h1>
          <div className="premium-spacer" />
        </header>
        <div className="manage-empty">
          <p>You don't have an active subscription.</p>
          <button
            className="cta-button"
            onClick={() => navigate('/premium')}
          >
            See Godly+ plans
          </button>
        </div>
      </div>
    );
  }

  const planInfo = PLAN_LABELS[status.plan] || PLAN_LABELS.monthly;

  return (
    <div className="premium-page">
      <header className="premium-header">
        <button className="premium-back" onClick={() => navigate(-1)} aria-label="Back">←</button>
        <h1>Subscription</h1>
        <div className="premium-spacer" />
      </header>

      <div className="manage-content">
        <div className="manage-card">
          <div className="manage-card-header">
            <span className="hero-badge">✨ Godly+</span>
            {status.cancel_at_period_end && (
              <span className="cancel-pending-badge">Ends soon</span>
            )}
          </div>

          <div className="manage-detail-row">
            <span className="detail-label">Plan</span>
            <span className="detail-value">{planInfo.name}</span>
          </div>
          <div className="manage-detail-row">
            <span className="detail-label">Price</span>
            <span className="detail-value">
              {formatNaira(planInfo.price)} <span className="detail-sub">{planInfo.period}</span>
            </span>
          </div>
          {status.renews_at && (
            <div className="manage-detail-row">
              <span className="detail-label">
                {status.cancel_at_period_end ? 'Access ends' : 'Next renewal'}
              </span>
              <span className="detail-value">
                {new Date(status.renews_at).toLocaleDateString('en-NG', {
                  year: 'numeric', month: 'long', day: 'numeric'
                })}
              </span>
            </div>
          )}
          <div className="manage-detail-row">
            <span className="detail-label">Payment method</span>
            <span className="detail-value">Flutterwave</span>
          </div>
        </div>

        {!status.cancel_at_period_end && (
          <button
            className="cta-button danger-link"
            onClick={() => setCancelConfirm(true)}
          >
            Cancel subscription
          </button>
        )}

        <p className="legal-text">
          Cancelling will keep your Godly+ access until the end of the current billing period.
          You won't be charged again.
        </p>
      </div>

      {cancelConfirm && (
        <div className="modal-overlay" onClick={() => setCancelConfirm(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2>Cancel Godly+?</h2>
            <p>
              You'll lose access to 20 likes per day, advanced filters, and seeing who liked you
              when your billing period ends on{' '}
              {status.renews_at && new Date(status.renews_at).toLocaleDateString()}.
            </p>
            <div className="modal-actions">
              <button
                className="modal-cancel"
                onClick={() => setCancelConfirm(false)}
              >
                Keep Godly+
              </button>
              <button
                className="modal-danger"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}